import { getInstance } from './auth/auth'

// Same backend base URL resolution as backend.js — kept separate (not exported
// from there) since this needs it turned into a ws(s):// URL, not an axios baseURL.
let baseURL = ''
if (window.appConfig?.VUE_APP_ENV === 'production') {
    baseURL = window.appConfig?.VUE_APP_BACKEND_URL
} else {
    baseURL = localStorage.getItem('nvBaseUrl')
    if (!baseURL) {
        baseURL = window.appConfig?.VUE_APP_BACKEND_URL
    }
}

const RECONNECT_DELAY_MS = 3000
// How long push may stay non-connected before standing polls resume.
// Covers WS open timeout (5s) + SSE attempt + a little slack for old backends
// that have no /internal/push/* at all. Temporary until push is considered stable.
export const PUSH_FALLBACK_GRACE_MS = 8000
const WS_OPEN_TIMEOUT_MS = 5000

let socket = null
let eventSource = null
let transport = null // 'ws' | 'sse' | null — once SSE is chosen, stick with it
let reconnectTimer = null
let connecting = false
let connectionStatus = 'idle' // 'idle' | 'connecting' | 'connected' | 'reconnecting'
let lastConnectedTs = 0
// Sticky for the session: true after the first successful WS/SSE open.
// Gates progressive-enhancement UI so old BE (no push) looks like pre-branch FE.
let pushUiEnabled = false
const pushUiListeners = new Set()
const statusListeners = new Set()

const listeners = new Map() // event name -> Set<callback>
// Fired when a transport becomes live (WS onopen / SSE onopen, including reconnects).
// App.vue uses this to resync deployment status instead of a tight poll loop.
const connectedListeners = new Set()

function httpBase() {
    return (baseURL || '').replace(/\/+$/, '')
}

function dispatchTo(event, data) {
    const callbacks = listeners.get(event)
    if (!callbacks) return
    for (const callback of callbacks) callback(data)
}

// Values carried in a user_notify frame's data.kind (dispatch() below re-fires each
// one under its own event name too, e.g. on(PushEventKind.TASK_FINISHED, ...)).
// Distinct from go-informer's own event kind (deployment/pod/service/... — the
// watched Kubernetes resource type, see InformerKind in nv-backend) — same field
// name, different vocabulary, different layer. Payload shapes mirror app/core/push.py's TypedDicts.
export const PushEventKind = {
    TASK_FINISHED: 'task_finished',
    DEPLOYMENT_STATUS_CHANGED: 'deployment_status_changed',
    APP_ALERT: 'app_alert',
    APP_AUTO_STOPPED: 'app_auto_stopped'
}

/**
 * @typedef {Object} TaskFinishedPayload
 * @property {string} tkid
 * @property {string} status - Task.status, e.g. "SUCCEEDED" / "FAILED"
 * @property {*} result
 */

/**
 * @typedef {Object} DeploymentStatusChangedPayload
 * @property {string} aid
 * @property {string} status - "STARTING" | "RUNNING" | "STOPPED" | "DELETING"
 */

/**
 * @typedef {Object} AppAlertPayload
 * @property {string} aid
 * @property {string} reason
 * @property {?string} message
 * @property {?string} containerName
 * @property {string} kind - the informer's kind, always "pod" today
 * @property {number} restartCount
 */

/**
 * @typedef {Object} AppAutoStoppedPayload
 * @property {string} aid
 * @property {?string} session_id
 * @property {'recreated'|'stopped'} action
 * @property {string} reason
 * @property {?string} message
 * @property {?string} containerName
 */

// user_notify frames are re-dispatched under their kind so existing
// on('task_finished')-style subscriptions keep working unchanged.
export function dispatch(event, data) {
    dispatchTo(event, data)
    if (event === 'user_notify' && data && typeof data.kind === 'string') {
        dispatchTo(data.kind, data.payload)
    }
}

function notifyConnected() {
    for (const cb of [...connectedListeners]) {
        try {
            cb(transport)
        } catch (err) {
            // ignore subscriber errors
        }
    }
}

function setStatus(next) {
    const prev = connectionStatus
    if (prev === next) return
    connectionStatus = next
    if (next === 'connected') {
        lastConnectedTs = Date.now()
        setPushUiEnabled(true)
    }
    const payload = { status: next, prev, transport, at: Date.now(), lastConnectedTs }
    for (const cb of [...statusListeners]) {
        try {
            cb(payload)
        } catch (err) {
            // ignore subscriber errors
        }
    }
}

export function getStatus() {
    return connectionStatus
}

// Subscribe to connection status transitions. Returns unsubscribe.
// Fires immediately with the current status so UI can paint without a race.
export function onStatus(cb) {
    statusListeners.add(cb)
    try {
        // Not an err-first Node callback — payload is the status snapshot.
        // eslint-disable-next-line n/no-callback-literal
        cb({ status: connectionStatus, prev: null, transport, at: Date.now(), lastConnectedTs })
    } catch (err) {
        // ignore
    }
    return () => statusListeners.delete(cb)
}

export function isPushConnected() {
    return (
        (typeof WebSocket !== 'undefined' && socket?.readyState === WebSocket.OPEN) ||
        (typeof EventSource !== 'undefined' && eventSource?.readyState === EventSource.OPEN)
    )
}

function setPushUiEnabled(next) {
    if (pushUiEnabled === next) return
    pushUiEnabled = next
    for (const cb of [...pushUiListeners]) {
        try {
            cb(pushUiEnabled)
        } catch (err) {
            // ignore
        }
    }
}

/** True after first successful push connect this session (sticky until logout). */
export function isPushUiEnabled() {
    return pushUiEnabled
}

/** Subscribe to sticky push-UI capability. Fires immediately. Returns unsubscribe. */
export function onPushUiEnabled(cb) {
    pushUiListeners.add(cb)
    try {
        cb(pushUiEnabled)
    } catch (err) {
        // ignore
    }
    return () => pushUiListeners.delete(cb)
}

// Subscribe to transport-up events (initial connect + reconnect). Returns unsubscribe.
// If already connected, cb runs once immediately.
export function onConnected(cb) {
    connectedListeners.add(cb)
    if (isPushConnected()) {
        try {
            cb(transport)
        } catch (err) {
            // ignore
        }
    }
    return () => connectedListeners.delete(cb)
}

/**
 * Temporary dual-compat gate: when push is not `connected` for `graceMs`,
 * call `cb(true)` so callers can resume standing polls; when push connects,
 * call `cb(false)` to stop them. Fires the current decision immediately
 * (false while still in grace / connected). Returns unsubscribe.
 *
 * Staging safety net so new FE still works against an old BE (no push) and
 * survives push outages without freezing app status. Nuke with the polls once
 * push is considered stable.
 */
export function onPushFallback(cb, { graceMs = PUSH_FALLBACK_GRACE_MS } = {}) {
    let graceTimer = null
    let fallbackActive = false

    function emit(active) {
        if (fallbackActive === active) return
        fallbackActive = active
        try {
            cb(active)
        } catch (err) {
            // ignore subscriber errors
        }
    }

    function clearGrace() {
        if (graceTimer != null) {
            clearTimeout(graceTimer)
            graceTimer = null
        }
    }

    function armGrace() {
        if (fallbackActive || graceTimer != null) return
        graceTimer = setTimeout(() => {
            graceTimer = null
            if (connectionStatus !== 'connected') emit(true)
        }, graceMs)
    }

    function handle(status) {
        if (status === 'connected') {
            clearGrace()
            emit(false)
            return
        }
        // idle | connecting | reconnecting — give push a chance first.
        armGrace()
    }

    const unsubStatus = onStatus(payload => handle(payload.status))
    return () => {
        clearGrace()
        unsubStatus()
    }
}

// Test-only status driver (no real socket). Production callers must not use this.
export function _setStatusForTests(next) {
    setStatus(next)
}

function clearReconnect() {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
}

function scheduleReconnect() {
    clearReconnect()
    // EventSource reconnects on its own; only schedule for WS (or initial connect).
    if (transport === 'sse') return
    // ±50% jitter avoids a reconnect stampede against a freshly restarted backend.
    const delay = Math.round(RECONNECT_DELAY_MS * (0.5 + Math.random()))
    reconnectTimer = setTimeout(() => {
        if (getInstance().isAuthenticated) connect()
    }, delay)
}

function teardownWs() {
    if (!socket) return
    const ws = socket
    socket = null
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    try {
        ws.close()
    } catch (err) {
        // ignore
    }
}

function teardownSse() {
    if (!eventSource) return
    const es = eventSource
    eventSource = null
    es.onmessage = null
    es.onerror = null
    try {
        es.close()
    } catch (err) {
        // ignore
    }
}

function openSse(token) {
    setStatus('connecting')
    teardownWs()
    teardownSse()
    transport = 'sse'
    connecting = false
    const url = `${httpBase()}/internal/push/sse?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    eventSource = es

    es.onopen = () => {
        // Fires on first connect and on EventSource auto-reconnect after a drop.
        setStatus('connected')
        notifyConnected()
    }
    es.onmessage = e => {
        try {
            const { event, data } = JSON.parse(e.data)
            dispatch(event, data)
        } catch (err) {
            // ignore malformed push message / keepalive comments never hit onmessage
        }
    }
    // EventSource auto-reconnects; no manual RECONNECT_DELAY_MS timer in SSE mode.
    es.onerror = () => {
        // browser handles reconnect; leave transport pinned to sse
        setStatus('reconnecting')
    }
}

// One shared socket for the whole app — see app/core/push.py/push_controller.py.
// Browsers can't set custom headers on a WebSocket handshake, so the JWT
// travels as a query param instead of the Authorization header axios uses.
// If WS never reaches OPEN (error/close before open, or 5s timeout), fall back
// to SSE and stay there until logout.
export async function connect() {
    if (!getInstance().isAuthenticated) return
    // Already live on either transport.
    if (socket?.readyState === WebSocket.OPEN || eventSource) return
    if (connecting) return

    let token
    try {
        token = await getInstance().getTokenSilently()
    } catch (err) {
        setStatus('reconnecting')
        scheduleReconnect()
        return
    }

    setStatus('connecting')

    // Once SSE was chosen for this login session, keep using it.
    if (transport === 'sse') {
        openSse(token)
        return
    }

    connecting = true
    clearReconnect()
    teardownWs()

    const wsURL = `${httpBase().replace(/^http/, 'ws')}/internal/push/stream?token=${encodeURIComponent(token)}`
    const ws = new WebSocket(wsURL)
    socket = ws
    let opened = false

    const failToSse = () => {
        if (opened || transport === 'sse') return
        opened = false
        connecting = false
        teardownWs()
        openSse(token)
    }

    const openTimer = setTimeout(failToSse, WS_OPEN_TIMEOUT_MS)

    ws.onopen = () => {
        opened = true
        connecting = false
        transport = 'ws'
        clearTimeout(openTimer)
        setStatus('connected')
        notifyConnected()
    }
    ws.onmessage = message => {
        try {
            const { event, data } = JSON.parse(message.data)
            dispatch(event, data)
        } catch (err) {
            // ignore malformed push message
        }
    }
    ws.onclose = () => {
        clearTimeout(openTimer)
        if (socket === ws) socket = null
        connecting = false
        if (!opened) {
            // Never reached OPEN → SSE fallback.
            failToSse()
            return
        }
        // Was open; schedule WS reconnect (transport stays 'ws').
        setStatus('reconnecting')
        scheduleReconnect()
    }
    ws.onerror = () => {
        // onclose follows; if never opened, failToSse runs from onclose/timeout.
        try {
            ws.close()
        } catch (err) {
            // ignore
        }
    }
}

// Close both transports and forget the session choice (login/logout / page unload).
export function disconnect() {
    clearReconnect()
    connecting = false
    transport = null
    teardownWs()
    teardownSse()
    setPushUiEnabled(false)
    setStatus('idle')
}

// Subscribe to a named push event (e.g. "task_finished", "deployment_status_changed").
// Frames arrive as user_notify envelopes with kind/payload and are re-dispatched
// under their kind (see dispatch above). Returns an unsubscribe function.
export function on(event, callback) {
    if (!listeners.has(event)) listeners.set(event, new Set())
    listeners.get(event).add(callback)
    return () => listeners.get(event)?.delete(callback)
}

// Resolve once with the next matching event, optionally filtered by predicate
// (e.g. matching a specific tkid out of the shared per-user stream).
export function once(event, predicate) {
    return new Promise(resolve => {
        const unsubscribe = on(event, data => {
            if (predicate && !predicate(data)) return
            unsubscribe()
            resolve(data)
        })
    })
}

// Like once(), but resolves with null after timeoutMs if no matching event
// arrives — always cleans up its listener either way. Use this instead of
// racing once() against a bare delay(), which would leave a dangling
// listener subscribed forever whenever the timeout wins the race.
export function onceOrTimeout(event, predicate, timeoutMs) {
    return new Promise(resolve => {
        let settled = false
        const unsubscribe = on(event, data => {
            if (settled || (predicate && !predicate(data))) return
            settled = true
            clearTimeout(timer)
            unsubscribe()
            resolve(data)
        })
        const timer = setTimeout(() => {
            if (settled) return
            settled = true
            unsubscribe()
            resolve(null)
        }, timeoutMs)
    })
}

// Best-effort teardown on tab close.
if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => disconnect())
}
